import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Email,
  Login,
  Send,
} from '@mui/icons-material';
import api from '../../services/api';

export const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setError('No verification token found.');
      setIsLoading(false);
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      await api.post('/studio-signup/verify-email', { token });
      setIsVerified(true);
      setIsLoading(false);
    } catch (error: any) {
      setError(
        error.response?.data?.message || 
        'Failed to verify email. The link may have expired.'
      );
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!resendEmail) {
      setError('Please enter your email address');
      return;
    }

    setResendLoading(true);
    setError(null);
    setResendSuccess(false);

    try {
      await api.post('/studio-signup/resend-verification', { 
        email: resendEmail 
      });
      setResendSuccess(true);
      setResendEmail('');
    } catch (error: any) {
      setError(
        error.response?.data?.message || 
        'Failed to resend verification email. Please try again.'
      );
    } finally {
      setResendLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0A0E1A',
        }}
      >
        <Card sx={{ 
          p: 4,
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(20px)',
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">Verifying your email...</Typography>
          </Box>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0A0E1A',
        backgroundImage: `
          radial-gradient(ellipse at top right, rgba(76, 175, 80, 0.1) 0%, transparent 50%),
          radial-gradient(ellipse at bottom left, rgba(255, 107, 53, 0.1) 0%, transparent 50%)
        `,
        padding: 2,
      }}
    >
      <Card sx={{ 
        maxWidth: 500, 
        width: '100%',
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      }}>
        <CardContent sx={{ p: 4 }}>
          {isVerified ? (
            <>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box sx={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  mb: 3,
                  padding: 3,
                  borderRadius: '50%',
                  background: 'rgba(76, 175, 80, 0.1)',
                  border: '2px solid rgba(76, 175, 80, 0.3)',
                }}>
                  <CheckCircle sx={{ fontSize: 60, color: 'success.main' }} />
                </Box>
                
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                  Email Verified!
                </Typography>
                
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Your email has been successfully verified. You can now sign in to your studio.
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => navigate('/login')}
                startIcon={<Login />}
                sx={{
                  background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                  boxShadow: '0 4px 20px rgba(255, 107, 53, 0.3)',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 24px rgba(255, 107, 53, 0.4)',
                  },
                }}
              >
                Go to Login
              </Button>
            </>
          ) : (
            <>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box sx={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  mb: 3,
                  padding: 3,
                  borderRadius: '50%',
                  background: 'rgba(244, 67, 54, 0.1)',
                  border: '2px solid rgba(244, 67, 54, 0.3)',
                }}>
                  <Error sx={{ fontSize: 60, color: 'error.main' }} />
                </Box>
                
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                  Verification Failed
                </Typography>
                
                {error && (
                  <Alert severity="error" sx={{ mt: 2, mb: 3 }}>
                    {error}
                  </Alert>
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Resend Verification Email
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Enter your email address to receive a new verification link.
                </Typography>
                
                {resendSuccess && (
                  <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                    Verification email sent! Please check your inbox.
                  </Alert>
                )}
                
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  margin="normal"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleResendVerification}
                  disabled={resendLoading || !resendEmail}
                  startIcon={resendLoading ? <CircularProgress size={20} /> : <Send />}
                  sx={{ mt: 2 }}
                >
                  {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                </Button>
              </Box>

              <Box sx={{ textAlign: 'center', pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="body2" color="text.secondary">
                  Already verified?{' '}
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => navigate('/login')}
                    sx={{ color: 'primary.main' }}
                  >
                    Sign in
                  </Button>
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};